import { useCallback, useEffect, useMemo } from 'react';
import type { ExtractionResultItem } from '../../../../../api/document-rag/document-rag.api.types';
import { useDocumentRagContextSafe } from '../../_contexts/DocumentRagContext';
import { useExtractFieldValue } from '../../_hooks/useExtractFieldValue';

/**
 * Hook for extracting field values using dynamic form field ID (UUID)
 * Used by form-engine components (Coverages tab)
 *
 * Safe to use outside DocumentRagProvider - returns disabled state when provider is absent.
 *
 * @param fieldId - The UUID of the dynamic form field
 * @param fieldLabel - Human-readable label for display in popover
 * @param submissionId - The submission ID
 * @param conversationId - Optional conversation ID for context
 * @param enabled - Whether RAG is enabled for this field type (default: true)
 */
export function useFieldExtractionById(
  fieldId: string,
  fieldLabel: string,
  submissionId: string,
  conversationId?: string,
  enabled = true
) {
  // Use safe context hook - returns null when provider is absent
  const context = useDocumentRagContextSafe();
  const messageId = context?.state.messageId;
  const dispatch = context?.dispatch;
  const contextConversationId = context?.state.conversationId;

  const {
    data: extraction,
    conversationId: responseConversationId,
    isLoading: queryIsLoading,
    isError,
    refetch,
    dataUpdatedAt,
  } = useExtractFieldValue({
    fieldId, // Use fieldId instead of field
    messageId: messageId || '',
    submissionId,
    conversationId,
    enabled: false, // Manual trigger only
  });

  // Handle new conversationId from API response - update context state
  useEffect(() => {
    if (
      responseConversationId &&
      dispatch &&
      responseConversationId !== contextConversationId
    ) {
      dispatch({
        type: 'SET_CONVERSATION_ID',
        payload: responseConversationId,
      });
    }
  }, [responseConversationId, contextConversationId, dispatch]);

  // Check if extraction can be triggered (all required params present)
  const canTrigger = Boolean(context && messageId && submissionId && enabled);

  /* Handlers */
  const triggerExtraction = useCallback(
    (options?: { force?: boolean }) => {
      if (!canTrigger) return;

      // If force is true, bypass cache and refetch immediately
      if (options?.force) {
        refetch();
        return;
      }

      // Check if we have fresh cached data (within 5 minutes)
      if (extraction && extraction.length > 0 && dataUpdatedAt) {
        const timeSinceUpdate = Date.now() - dataUpdatedAt;
        const staleTimeMs = 5 * 60 * 1000; // 5 minutes
        if (timeSinceUpdate < staleTimeMs) {
          // Data is still fresh, don't refetch
          return;
        }
      }
      // Data is stale or missing, refetch
      refetch();
    },
    [canTrigger, refetch, extraction, dataUpdatedAt]
  );

  // Transform all DTOs to UI type by adding the field property from label
  const extractionResults: ExtractionResultItem[] = useMemo(() => {
    if (!extraction || extraction.length === 0) {
      return [];
    }
    return extraction.map((item) => ({
      ...item,
      field: fieldLabel, // Use the label for display purposes
    }));
  }, [extraction, fieldLabel]);

  // Return single item if only one, array if multiple, undefined if none
  const extractionResult:
    | ExtractionResultItem
    | ExtractionResultItem[]
    | undefined =
    extractionResults.length === 0
      ? undefined
      : extractionResults.length === 1
      ? extractionResults[0]
      : extractionResults;

  return {
    extraction: extractionResult,
    loading: queryIsLoading, // Named 'loading' to match FieldExtractionPopover props
    isError,
    triggerExtraction,
    disabled: !canTrigger,
    show: canTrigger,
  };
}
